import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "../App";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Sparkles, Copy, ExternalLink, Trophy, Flame } from "lucide-react";
import TrustStars from "./TrustStars";
import { countryToFlag } from "../lib/countryFlag";
import AccaROITile from "./AccaROITile";

/**
 * Iter 95 — One-tap "HT BTTS Acca" shortcut
 *
 * Pulls /api/acca/ht-btts?legs=N (default 4, slider 2-7) — HT BTTS is
 * empirically our best-calibrated market (Iter 93 backtest: 82% hit rate
 * over 267 settled fixtures, Brier 0.148).  We surface a curated N-leg
 * accumulator on the homepage so members can place a high-confidence
 * combo with one tap.
 *
 * - Default 4 legs (≈45% combined hit rate, 1.0/0.45 = 2.2× implied odds)
 * - Trust Score sort, max 1 leg per league for diversification
 * - "Place on Betway" routes to the multi-bet builder (or per-leg event
 *   pages when betway_href is available); "Copy" exports plaintext.
 */
const HTBTTSAccaCard = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [legs, setLegs] = useState(4);
  const [day, setDay] = useState("today");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    axios
      .get(`${API}/acca/ht-btts`, { params: { legs, day }, timeout: 30000 })
      .then((r) => {
        if (!cancel) setData(r.data);
      })
      .catch(() => {
        if (!cancel) setData(null);
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [legs, day]);

  const handleCopy = () => {
    if (!data?.legs?.length) return;
    const lines = [
      `🔥 NDAKUBET HT BTTS Acca — ${data.actual_legs} legs · combined ${data.combined_probability_pct}%`,
      `Implied odds: ${data.implied_odds}×`,
      "",
      ...data.legs.map(
        (l, i) =>
          `${i + 1}. ${l.home_team} vs ${l.away_team} (${l.league_name}) — HT BTTS ${l.prediction} @ ${l.confidence}%`
      ),
      "",
      "Generated by NDAKUBET · ndakubet.com",
    ].join("\n");
    navigator.clipboard?.writeText(lines).then(
      () => toast.success("Slip copied to clipboard"),
      () => toast.error("Couldn't copy — long-press to copy manually"),
    );
  };

  const recordPlacement = async () => {
    if (!token || !data?.legs?.length) return;
    try {
      await axios.post(
        `${API}/acca/place`,
        {
          legs: data.legs.map((l) => ({
            fixture_id: l.fixture_id,
            home_team: l.home_team,
            away_team: l.away_team,
            market_type: l.market_type,
            prediction: l.prediction,
            confidence: l.confidence,
          })),
          combined_probability_pct: data.combined_probability_pct,
          implied_odds: data.implied_odds,
          stake: 0,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch {
      // silent — placement recording is best-effort, the user already opened Betway
    }
  };

  const handlePlaceBetway = () => {
    if (!data?.legs?.length) return;
    const withHref = data.legs.filter((l) => l.betway_href);
    if (withHref.length === 0) {
      toast.info("None of these fixtures are mapped to Betway yet — try Copy and add manually");
      return;
    }
    withHref.forEach((l) => window.open(l.betway_href, "_blank", "noopener,noreferrer"));
    toast.success(`Opened ${withHref.length} Betway event page${withHref.length > 1 ? "s" : ""}`);
    recordPlacement();
  };

  return (
    <div
      className="relative overflow-hidden rounded-3xl border border-amber-500/20 bg-gradient-to-br from-amber-500/5 via-background to-emerald-500/5 p-6 sm:p-8 mb-12"
      data-testid="ht-btts-acca-card"
    >
      {/* Glow accents */}
      <div className="absolute -top-32 -right-32 w-64 h-64 rounded-full bg-amber-500/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-56 h-56 rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" />

      <div className="relative">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-[0.12em] uppercase bg-amber-500/15 text-amber-600 border border-amber-500/30">
                <Flame className="w-3 h-3" /> Casino-grade signal
              </span>
              <span className="text-[10px] font-bold tracking-[0.12em] uppercase text-muted-foreground">
                Iter 95
              </span>
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight inline-flex items-center gap-2">
              <Trophy className="w-6 h-6 text-amber-500" />
              HT BTTS Acca
            </h2>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-md">
              Best-calibrated market on the platform —{" "}
              <span className="font-bold text-foreground">82% hit rate</span> over the
              last 30 days (n=267, Brier 0.148).
            </p>
          </div>

          {/* Day toggle */}
          <div className="inline-flex rounded-full border border-border/60 p-1 bg-background/40 backdrop-blur-sm">
            {["today", "tomorrow"].map((d) => (
              <button
                key={d}
                onClick={() => setDay(d)}
                data-testid={`acca-day-${d}`}
                className={`px-3 py-1.5 text-[11px] font-bold tracking-[0.06em] uppercase rounded-full transition-colors ${
                  day === d
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>

        {/* Legs slider */}
        <div className="flex items-center justify-between gap-4 mb-5">
          <label
            htmlFor="acca-legs-slider"
            className="text-[11px] font-bold tracking-[0.06em] uppercase text-muted-foreground"
          >
            Legs ({legs})
          </label>
          <input
            id="acca-legs-slider"
            type="range"
            min={2}
            max={7}
            value={legs}
            onChange={(e) => setLegs(parseInt(e.target.value, 10))}
            className="flex-1 max-w-md accent-amber-500"
            data-testid="acca-legs-slider"
            aria-label="Number of acca legs"
          />
        </div>

        {/* Combined odds tile */}
        {data && data.combined_probability_pct !== null && (
          <>
            <AccaROITile />
            <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="rounded-xl border border-border/40 bg-background/40 backdrop-blur-sm p-3">
              <div className="text-[10px] font-bold tracking-[0.12em] uppercase text-muted-foreground mb-1">
                Combined
              </div>
              <div className="text-2xl font-display font-bold text-amber-500">
                {data.combined_probability_pct}%
              </div>
            </div>
            <div className="rounded-xl border border-border/40 bg-background/40 backdrop-blur-sm p-3">
              <div className="text-[10px] font-bold tracking-[0.12em] uppercase text-muted-foreground mb-1">
                Implied odds
              </div>
              <div className="text-2xl font-display font-bold">{data.implied_odds}×</div>
            </div>
            <div className="rounded-xl border border-border/40 bg-background/40 backdrop-blur-sm p-3">
              <div className="text-[10px] font-bold tracking-[0.12em] uppercase text-muted-foreground mb-1">
                Expected hit
              </div>
              <div className="text-2xl font-display font-bold text-emerald-500">
                {data.expected_combined_hit_rate_pct}%
              </div>
            </div>
          </div>
          </>
        )}

        {/* Legs list */}
        {loading && (
          <div className="space-y-2 mb-5">
            {[...Array(legs)].map((_, i) => (
              <div key={i} className="h-12 rounded-lg bg-muted/40 animate-pulse" />
            ))}
          </div>
        )}

        {!loading && data?.legs?.length > 0 && (
          <ul className="space-y-2 mb-5" data-testid="acca-legs-list">
            {data.legs.map((leg, i) => (
              <li
                key={leg.fixture_id}
                onClick={() => navigate(`/match/${leg.fixture_id}`)}
                className="group flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border/40 bg-background/40 backdrop-blur-sm hover:border-amber-500/40 hover:bg-amber-500/5 transition-colors cursor-pointer"
                data-testid={`acca-leg-${i}`}
              >
                <span className="font-display font-bold text-amber-500 text-sm w-5">
                  {i + 1}
                </span>
                <span className="text-base">
                  {countryToFlag(leg.league_country) || "🌍"}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold leading-snug truncate">
                    {leg.home_team} <span className="text-muted-foreground">vs</span>{" "}
                    {leg.away_team}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {leg.league_name} · HT BTTS{" "}
                    <span className="font-bold text-foreground">{leg.prediction}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <TrustStars score={leg.trust_score} components={leg.trust_components} size="xs" />
                  <span className="font-display font-bold text-amber-500 text-base tabular-nums">
                    {leg.confidence}%
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}

        {!loading && data?.legs?.length > 0 && data.confidence_relaxed && (
          <div
            className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1 text-[11px] font-semibold text-amber-700 dark:text-amber-300"
            data-testid="acca-floor-relaxed"
            title="Today's strongest picks are below the usual 70% floor — showing the best available."
          >
            ✨ Adapted floor · {Math.round(data.effective_confidence_floor)}%+
          </div>
        )}

        {!loading && (!data?.legs || data.legs.length === 0) && (
          <p className="text-sm text-muted-foreground italic mb-5">
            No HT BTTS picks ≥{data?.requested_confidence_floor ? Math.round(data.requested_confidence_floor) : 70}% confidence available right now. Check back closer to kickoff.
          </p>
        )}

        {/* CTAs */}
        {data?.legs?.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handlePlaceBetway}
              data-testid="acca-place-betway"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold tracking-[0.04em] uppercase transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Place on Betway
            </button>
            <button
              onClick={handleCopy}
              data-testid="acca-copy"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-border bg-background/40 hover:border-foreground/40 text-sm font-bold tracking-[0.04em] uppercase transition-colors"
            >
              <Copy className="w-4 h-4" />
              Copy slip
            </button>
            <span className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] text-muted-foreground">
              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
              {data.actual_legs}-leg combo · {data.day}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default HTBTTSAccaCard;
